// FB(Flexi Bag) 전용 PKG 코드 제한 공용 헬퍼 — U/R 000001699733
// 참고 : docs/Flexibag_PKG코드_제한_영향도_20260911.md (§14 최신 사양)
//
// 비FB 부킹에서
//   ① Package 콤보에 FB 전용 코드를 노출하지 않는다
//   ② Mark & Description 자유 텍스트에 FB 문구를 새로 입력할 수 없다
// 를 처리한다. 적용 화면은 BookingNewSr.vue / WorkingBL.vue 두 곳.
//
// 주의
//  - FB 판정은 반드시 "부킹" 기준이다. 화면의 컨테이너 Cargo Type 은 화주가 바꿀 수 있어
//    그 값으로 판정하면 1클릭 우회가 된다. (BookingNewSr 은 부킹 생성 중이라 화면값이 곧 신고값)
//  - 적용 오피스 판정 축이 화면마다 다르다.
//    BookingNewSr = 회원 부킹지(memberDetail.bkgPlcCd) / WorkingBL = 부킹의 BKG_PLC_CD
//  - 공통코드가 아직 등록되지 않았으면 제한이 전혀 걸리지 않는다(빈 목록 = 무제한).
//    배포와 코드 등록 순서를 자유롭게 두기 위한 의도된 동작이다.

// 공통코드 ID
export const CD_FB_PKG = '01400' // FB 전용 PKG 코드 (FX / FT / 37 / 02)
export const CD_FB_RESTRICT_PLC = '01401' // 제한 적용 부킹오피스 (SZP)

// 부킹 특수화물 구분(공통코드 01038) 중 FB
export const CGO_TYP_FB = '06'

// 영숫자 외(공백·하이픈·줄바꿈 등)를 모두 제거하고 대문자로 맞춘다.
// 화주가 "FLEXIBAG" / "Flexi-Bag" / "FLEXI BAG" 을 섞어 쓰므로 양쪽을 같은 형태로 만들어 비교한다.
// BookingNewSr.isLithiumBatteryShow 가 품명을 비교할 때 쓰는 방식과 같다.
export function normalizeText (text) {
  return String(text === null || text === undefined ? '' : text)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
}

// 공통코드 목록을 안전하게 꺼낸다. 미등록이면 빈 배열.
function codeList (commonCodes, cdId) {
  if (commonCodes === null || commonCodes === undefined) {
    return []
  }
  const list = commonCodes[cdId]
  return Array.isArray(list) ? list : []
}

// FB 전용 PKG 코드 목록
export function fbPkgCodes (commonCodes) {
  return codeList(commonCodes, CD_FB_PKG)
}

// 제한을 적용하는 부킹오피스인가
export function isFbRestrictPlc (commonCodes, plcCd) {
  if (!plcCd) {
    return false
  }
  return codeList(commonCodes, CD_FB_RESTRICT_PLC).some(vo => vo.cd === plcCd)
}

// FB 로 신고된 부킹인가.
// list 는 cgoTypCd 를 가진 배열 — WorkingBL 은 regParams.cgoTypList(= CS002I),
// BookingNewSr 은 regParams.containerList(부킹 생성 중이라 화면 선택이 곧 신고값)
export function isFbBooking (list) {
  if (!Array.isArray(list)) {
    return false
  }
  return list.some(vo => vo && vo.cgoTypCd === CGO_TYP_FB)
}

// 콤보 표시용 목록. 원본 공통코드 배열은 절대 변형하지 않는다.
// keepCd 는 이미 저장되어 있는 값 — 목록에서 사라지면 기존 건 재저장이 막히므로 예외로 남긴다.
export function filterFbPkgList (pkgList, fbCodes, keepCd) {
  if (!Array.isArray(pkgList) || !Array.isArray(fbCodes) || fbCodes.length === 0) {
    return Array.isArray(pkgList) ? pkgList : []
  }
  return pkgList.filter(vo =>
    !fbCodes.some(fb => fb.cd === vo.cd) || vo.cd === keepCd
  )
}

// 새로 들어온 FB 전용 PKG 코드를 찾는다. 걸린 코드값을 돌려주고, 없으면 빈 문자열.
//
// beforeCds 는 조회 시점에 이미 쓰이고 있던 코드들이다.
// 본 개선 이전에 FB 코드로 저장된 건은 그대로 두고, 새로 지정한 경우만 막는다.
// 콤보에서 저장값을 예외로 남겨두는 것(filterFbPkgList 의 keepCd)과 짝이 맞아야 한다 —
// 콤보에는 보이는데 저장은 막히면 그 건은 영원히 수정할 수 없다.
export function findNewFbPkgCd (fbCodes, nowCds, beforeCds) {
  if (!Array.isArray(fbCodes) || fbCodes.length === 0) {
    return ''
  }
  const now = Array.isArray(nowCds) ? nowCds : []
  const before = Array.isArray(beforeCds) ? beforeCds : []

  const hit = now.find(cd =>
    cd && fbCodes.some(fb => fb.cd === cd) && before.indexOf(cd) === -1
  )

  return hit === undefined ? '' : hit
}

// 본문 비교용 표준 패턴.
// 공통코드에는 FLEXI BAG / FLEXIBLE BAG / FLEXIBLE BAGS / FLEXITANK가 각각
// 등록돼 있지만, 본문에서는 단수·복수·공백·하이픈이 섞여 같은 표현으로 쓰인다.
// 코드명 자체는 안내 문구에 사용하고, 비교할 때만 세 패턴으로 통합한다.
function fbWordPattern (cdNm) {
  const word = normalizeText(cdNm)

  if (word.indexOf('FLEXIBLEBAG') === 0) {
    return 'FLEXIBLEBAG'
  }
  if (word.indexOf('FLEXIBAG') === 0) {
    return 'FLEXIBAG'
  }
  if (word.indexOf('FLEXITANK') === 0) {
    return 'FLEXITANK'
  }

  return word
}

// 검사 대상 텍스트를 하나로 이어 붙인다.
// 행 추가·삭제로 배열 인덱스가 밀리므로 항목별 비교를 하지 않고 전문(全文)으로 비교한다.
export function joinTextForCheck (parts) {
  if (!Array.isArray(parts)) {
    return ''
  }
  return parts.filter(v => v !== null && v !== undefined && v !== '').join(' ')
}

// 금지 문구 탐지. 걸린 코드명을 그대로 돌려주고, 없으면 빈 문자열.
//
// beforeText 를 넘기면 "지금은 있고 원본에는 없던" 문구만 잡는다(= 변경분만 검사).
// 본 개선 이전에 저장된 건은 그대로 두고, 새로 입력·수정한 경우만 막기 위한 것이다.
// 신규 건은 beforeText 가 비어 있으므로 전건 검사가 된다.
export function findFbWord (fbCodes, nowText, beforeText) {
  if (!Array.isArray(fbCodes) || fbCodes.length === 0) {
    return ''
  }
  const now = normalizeText(nowText)
  if (now === '') {
    return ''
  }
  const before = normalizeText(beforeText)

  // 공통코드의 단수·복수 코드를 본문 비교용 세 패턴으로 합친다.
  const sorted = fbCodes
    .filter(vo => vo && vo.cdNm)
    .slice()
    .map(vo => ({ vo, word: fbWordPattern(vo.cdNm) }))
    .filter(item => item.word !== '')
    .sort((a, b) => b.word.length - a.word.length)

  const hit = sorted.find(item => {
    const word = item.word
    return word !== '' && now.indexOf(word) > -1 && before.indexOf(word) === -1
  })

  return hit === undefined ? '' : hit.vo.cdNm
}
